import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, combineLatest, ReplaySubject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { tap, takeUntil, filter, switchMap, map, take, shareReplay, distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Profile } from 'src/app/user/user.model';
import { profileSelector } from 'src/app/user/store/user.selectors';
import { IframeModuleService } from '../../services/iframe-module/iframe-module.abstract.service';
import { languageSelector, organisationSelector } from '../../store/global.selectors';

@Component({
    selector: 'app-iframe-page',
    templateUrl: './iframe.component.html',
    styleUrls: ['./iframe.component.scss'],
})
export class IframePageComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    private update$ = new ReplaySubject();

    loading = false;

    idToken$: Observable<string> = this.oidcService.isAuthenticated$.pipe(
        filter(({ isAuthenticated }) => isAuthenticated),
        take(1),
        switchMap(() => this.oidcService.getAccessToken()),
    );

    lang$: Observable<string> = this.store.select(languageSelector);

    configuration$: Observable<any> = this.serivce.getConfiguration().pipe(shareReplay(1));

    profile$: Observable<Profile> = this.store.select(profileSelector);

    iframeSrc: string;

    @Input('moduleId') moduleId: string;

    constructor(
        private translate: TranslateService,
        private store: Store<State>,
        private route: ActivatedRoute,
        private chRef: ChangeDetectorRef,
        private serivce: IframeModuleService,
        private oidcService: OidcSecurityService,
    ) {}

    ngOnInit(): void {
        this.update$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => (this.loading = true)),
                switchMap(() =>
                    combineLatest([this.store.select(organisationSelector), this.route.data]).pipe(
                        filter(([org, data]) => !!org),
                        take(1),
                        switchMap(([org, routeData]) => {
                            if (!this.moduleId) {
                                this.moduleId = routeData.moduleId;
                            }
                            if (!this.moduleId) {
                                console.error('bad moduleId', this.moduleId);
                                this.loading = false;
                                this.chRef.detectChanges();
                                return [];
                            }

                            let resolvedModuleId = this.moduleId;
                            if (this.moduleId === 'sdp-parking' || this.moduleId === 'sdp-parking-permit') {
                                if (org.tag === 'SHERBROOKE') {
                                    resolvedModuleId = `${this.moduleId}-sherbrooke`;
                                }
                            }

                            return this.buildUrl$(resolvedModuleId);
                        }),
                    ),
                ),
            )
            .subscribe();

        this.translate.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap(() => this.update$.next()),
            )
            .subscribe();

        // A new sub-path (`/communications/...`) reloads the iframe on it.
        this.route.paramMap
            .pipe(
                takeUntil(this.destroy$),
                map((params) => params.get('modulePath')),
                distinctUntilChanged(),
                tap(() => this.update$.next()),
            )
            .subscribe();
    }

    private buildUrl$(moduleId: string): Observable<void> {
        return combineLatest([this.profile$, this.idToken$, this.lang$, this.configuration$]).pipe(
            takeUntil(this.destroy$),
            filter(([profile, token, lang, config]) => !!profile && !!token && !!lang && !!config),
            take(1),
            map(([profile, token, lang, config]) => {
                this.iframeSrc = '';
                if (config[moduleId]) {
                    let tempUrl = config[moduleId].host;
                    const modulePath = this.route.snapshot.paramMap.get('modulePath');
                    if (modulePath) {
                        const queryStart = tempUrl.includes('?') ? tempUrl.indexOf('?') : tempUrl.length;
                        tempUrl = `${tempUrl.slice(0, queryStart).replace(/\/$/, '')}/${modulePath}${tempUrl.slice(queryStart)}`;
                    }
                    tempUrl = tempUrl.replace('{{token}}', token);
                    tempUrl = tempUrl.replace('{{lang}}', lang);
                    tempUrl = tempUrl.replace('{{userId}}', profile.id);
                    tempUrl = tempUrl.replace('{{email}}', profile.email);
                    tempUrl = tempUrl.replace('{{firstName}}', profile.firstName);
                    tempUrl = tempUrl.replace('{{lastName}}', profile.lastName);
                    tempUrl = tempUrl.replace('{{sex}}', profile.sex);
                    tempUrl += `&env=${environment.label}`;
                    tempUrl += `&date=${Date.now()}`;
                    this.iframeSrc = tempUrl;
                } else {
                    console.error(`iframe module config not found for key: "${moduleId}"`);
                }
                this.loading = false;
                this.chRef.detectChanges();
            }),
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
